import _ from 'lodash'
import { map as awaityMap } from 'awaity/esm'

// constants
import { PROPERTY_VARIABLE_TYPES } from 'constants/filter'

// utils
import {
  getImagesProperties,
  getPropertyDisplayNameFromMetadata,
} from 'helpers/asset'
import { displayValue, isVideoFile } from 'helpers/utils'
import log, { reportErrors } from 'helpers/log'
import { AssetProperties } from 'types/asset'
import { PropertiesMetadata } from 'types/common'
import { Timezone } from 'types/datetime'

import type { ImageType } from 'components/common/ImageGallery'
import type { DataCollectionFormMediaView } from 'types/issue'

export const NO_IMAGES_CONTENT = 'No Data'

const addImageProcess = (
  src: string
): Promise<{
  width?: number
  height?: number
}> => {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = () => resolve({})
    img.src = src
  })
}

const getLabel = ({
  propertiesMetadataKeyByName,
  properties,
  timezone,
  propertyName,
}) => {
  if (!propertyName) return undefined

  const displayName = getPropertyDisplayNameFromMetadata({
    propertiesMetadataKeyByName,
    propertyName,
  })
  const propertyValue = displayValue(properties[propertyName], timezone)
  return `${displayName}: ${propertyValue}`
}

const getMediaList = ({
  properties,
  title,
  subtitle,
  filteredMediaMetadata,
  propertiesMetadataKeyByName,
  isVideo = false,
}) =>
  _.flatMap(filteredMediaMetadata, ({ name, type }) => {
    const property = getPropertyDisplayNameFromMetadata({
      propertiesMetadataKeyByName,
      propertyName: name,
    })
    const src = properties[name]

    const commonPayload = {
      title,
      subtitle,
      property,
    }

    if (type === PROPERTY_VARIABLE_TYPES.array) {
      if (_.isArray(src)) {
        return _(src)
          .filter(imageSrc => {
            if (!imageSrc) return false
            const isVideoSource = isVideoFile(new URL(imageSrc).pathname)
            // If we need only videos, keep only URLs pointing to video files
            return isVideo ? isVideoSource : !isVideoSource
          })
          .map((imageSrc, idx) => ({
            ...commonPayload,
            imageSrc,
            title: title && `${title}-${idx}`,
            subtitle: subtitle && `${subtitle}-${idx}`,
          }))
          .value()
      }
      log.error(
        '[Asset]Image metadata type is array, but the src is an empty array.'
      )
      return []
    }

    return {
      ...commonPayload,
      imageSrc: src,
    }
  })

const fetchImage = async ({
  imageSrc,
  ...restImageProperties
}: {
  imageSrc: string
  title?: string
  subtitle: string
}): Promise<ImageType> => {
  const { title, subtitle } = restImageProperties
  const { width, height } = await addImageProcess(imageSrc)
  if (!width || !height) {
    reportErrors(
      `Forbidden to access asset image ${title}-${subtitle}(${imageSrc})`
    )
  }

  return {
    ...restImageProperties,
    src: imageSrc,
    width: width || 4,
    height: height || 3,
  }
}

export type GetAssetImageItems = {
  properties?: AssetProperties
  propertiesMetadata?: PropertiesMetadata
  visibleProperties: string[]
  titleProperty?: string
  subtitleProperty?: string
  timezone?: Timezone
  isVideo?: boolean
}

const getFilteredMediaMetadata = ({
  propertiesMetadata,
  visibleProperties,
  properties,
}: {
  propertiesMetadata?: PropertiesMetadata
  visibleProperties: string[]
  properties?: AssetProperties
}) => {
  const mediaMetadata = getImagesProperties({ propertiesMetadata })
  const filteredMediaMetadata = mediaMetadata.filter(({ name }) => {
    return _.includes(visibleProperties, name) && properties?.[name as string]
  })
  const propertiesMetadataKeyByName = _.keyBy(propertiesMetadata, 'name')

  return { filteredMediaMetadata, propertiesMetadataKeyByName }
}

export const getAssetImageItems = async ({
  properties = {},
  propertiesMetadata = [],
  visibleProperties,
  titleProperty,
  subtitleProperty,
  timezone,
}: GetAssetImageItems): Promise<ImageType[]> => {
  const { filteredMediaMetadata, propertiesMetadataKeyByName } =
    getFilteredMediaMetadata({
      propertiesMetadata,
      visibleProperties,
      properties,
    })

  const imgTitle = getLabel({
    propertiesMetadataKeyByName,
    properties,
    timezone,
    propertyName: titleProperty,
  })
  const imgSubtitle = getLabel({
    propertiesMetadataKeyByName,
    properties,
    timezone,
    propertyName: subtitleProperty,
  })

  const toBeFetchedImageList = getMediaList({
    properties,
    title: imgTitle,
    subtitle: imgSubtitle,
    filteredMediaMetadata,
    propertiesMetadataKeyByName,
  })

  return awaityMap(toBeFetchedImageList, fetchImage)
}

export const getAssetVideoItems = ({
  propertiesMetadata,
  visibleProperties,
  properties,
}: {
  propertiesMetadata?: PropertiesMetadata
  visibleProperties: string[]
  properties?: AssetProperties
}): DataCollectionFormMediaView[] => {
  const { filteredMediaMetadata, propertiesMetadataKeyByName } =
    getFilteredMediaMetadata({
      propertiesMetadata,
      visibleProperties,
      properties,
    })

  const videoUrls = getMediaList({
    properties,
    filteredMediaMetadata,
    propertiesMetadataKeyByName,
    isVideo: true,
  })

  return videoUrls.map(({ imageSrc }) => {
    const imgUrl = new URL(imageSrc)
    const mediaKey = _.last(imgUrl.pathname.split('/')) || imgUrl.pathname

    return {
      source: imageSrc,
      downloadUrl: imageSrc,
      mediaKey,
      modifiedAt: new Date().toISOString(),
    }
  })
}
